import type { ReportVersionMeta } from '@/types';
import { cn } from '@/lib/utils';
import { Badge } from '@astryxdesign/core/Badge';
import { Dialog } from '@astryxdesign/core/Dialog';
import { formatTimestamp } from '../lib/format';

interface HistoryDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  dateLabel: string;
  versions: ReportVersionMeta[];
  latestVersion: number | null;
  viewingVersion: number | null;
  onView: (version: number) => void;
}

export default function HistoryDialog({
  isOpen,
  onOpenChange,
  dateLabel,
  versions,
  latestVersion,
  viewingVersion,
  onView,
}: HistoryDialogProps) {
  const newestFirst = [...versions].reverse();

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} width={430}>
      <div className="p-5">
        <div className="border-border border-b pb-4">
          <h2 className="text-primary text-base font-extrabold">Versiones guardadas</h2>
          <p className="text-secondary mt-0.5 text-sm">{dateLabel}</p>
        </div>
        <div className="mt-3 flex max-h-[60vh] flex-col gap-2 overflow-y-auto">
          {newestFirst.map((meta) => {
            const isActive = viewingVersion === meta.version;
            const isCurrent = meta.version === latestVersion && viewingVersion == null;

            return (
              <button
                key={meta.version}
                type="button"
                onClick={() => onView(meta.version)}
                className={cn(
                  'flex w-full cursor-pointer items-center justify-between gap-3 rounded-xl border p-3 text-left transition-colors',
                  isActive ? 'border-accent bg-accent-bg' : 'border-border bg-card hover:bg-surface'
                )}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={cn(
                      'flex size-10 flex-none items-center justify-center rounded-lg text-sm font-extrabold',
                      isActive ? 'bg-accent text-on-accent' : 'bg-blue-subtle text-blue-vivid'
                    )}
                  >
                    v{meta.version}
                  </span>
                  <div>
                    <div className="text-primary text-sm font-bold">
                      {formatTimestamp(meta.savedAt)}
                    </div>
                    <div className="text-secondary text-xs">{meta.summary}</div>
                  </div>
                </div>
                {isCurrent && <Badge label="Actual" variant="success" />}
              </button>
            );
          })}
        </div>
      </div>
    </Dialog>
  );
}
